import { classNames, Component, h, signal, tag } from "omi";
import every from "lodash-es/every";

import { AvatarStack } from "@/wc/pages/project/features/directory/components/avatarStack";
import { ONBOARDING_HIDE_BG } from "@/globals/storageKeys.ts";
import { Plan, SearchResult, SearchResultEntity } from "@/api";
import { NWProBadge, openModal } from "@/wc/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { DirectoryStore } from "../../store";
import { getMetadata, getUrlForProject, isActiveProject } from "../../utils";
import { ProjectStatusIndicator } from "../projectStatusIndicator";
import { SelectedStatus } from "../selectButton";

type ProjectProps = {
  type: "project";
} & SearchResult;

type Props = ProjectProps;

@tag("nw-directory-search-list-item")
export class ListItem extends Component<Props> {
  static css = [tailwind];

  private isSelected = signal<boolean>(false);

  receiveProps(props: Props) {
    this.isSelected.value = every(props.entities, e => getMetadata(e).selected === "true");
  }

  private handleSelection = (event: MouseEvent, entities: SearchResultEntity[], selected: boolean) => {
    event.preventDefault();

    if (!Store.user.isLoggedIn.value) {
      return openModal("login", {});
    }

    this.isSelected.value = selected;
    const ids = entities.map(e => e.id);

    DirectoryStore.updateRoadmap(ids, selected, () => {
      this.isSelected.value = !selected;
    });

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackDirectoryAddToMyProjectsClicked("project", ids, selected ? "added" : "removed");
    });
  };

  private handleCategoryClick = (event: MouseEvent, query: string | undefined) => {
    event.preventDefault();

    if (!query) return;
    DirectoryStore.search(query);
  };

  private createProjectUrl(id: string) {
    if (Store.user.isLoggedIn.value) {
      return getUrlForProject(id);
    }

    return getUrlForProject(id, { [ONBOARDING_HIDE_BG]: "true" });
  }

  renderProject(props: ProjectProps, embedded: boolean) {
    const project = props.entities![0];
    const id = project.id;
    const title = props.title;
    const metadata = getMetadata(project);

    const seriesPart = metadata.part;
    const status = metadata.status;
    const category = metadata.category;
    const isProProject = metadata.plan === Plan.PRO;
    const progress = metadata.progress;
    const isSelected = metadata.selected === "true" || this.isSelected.value;

    const completions = metadata.completions || 0;
    const userAvatars = metadata.completedBy || [];

    return (
      <div
        className={classNames(
          "animate-fadeInFast group flex flex-row py-3 justify-between transition-all hover:bg-primary rounded-[20px]",
          { "px-3": !embedded },
        )}
      >
        <div className="flex flex-col space-y-1 px-3 w-fit truncate">
          <a
            href={this.createProjectUrl(id!)}
            className={"text-lg text-primary truncate hover:underline hover:underline-offset-2"}
          >
            {title}
          </a>
          <div className="flex items-center gap-2  select-none">
            <ProjectStatusIndicator status={status} progress={progress} isActive={isActiveProject(id)} size="lg" />
            <button
              className="inline text-sm text-quaternary hover:underline hover:underline-offset-2"
              onClick={event => this.handleCategoryClick(event, category)}
            >
              {category} {seriesPart != null ? `(Part ${seriesPart})` : ""}
            </button>
            {completions > 0 && userAvatars.length > 0 && (
              <>
                <span className="text-sm text-tertiary hidden sm:inline-block">•</span>
                <span className="hidden sm:inline-block">
                  <AvatarStack avatarUrls={userAvatars} completionCount={completions} size="xs" />
                </span>
              </>
            )}

            {isProProject && (
              <>
                <span className="text-sm text-tertiary">•</span>
                <span className="text-sm text-tertiary">
                  <NWProBadge location="directory-project" analyticsData={{ project: project.id }} />
                </span>
              </>
            )}
          </div>
        </div>

        <div
          className={classNames("flex items-center", "group-hover:opacity-100", {
            "opacity-0 transition-opacity duration-300 ease-in-out": !isSelected,
          })}
        >
          <SelectedStatus
            isSelected={isSelected}
            onToggle={e => this.handleSelection(e, props.entities!, !isSelected)}
          />
        </div>
      </div>
    );
  }

  render(props: Props) {
    return this.renderProject(props as ProjectProps, false);
  }
}
